/**
 * Dialog for editing agent-facing metadata on a linked account/API key.
 *
 * These fields live in `user_external_identity.metadata`; they do not touch the
 * stored OAuth tokens or static credential value.
 */

import { useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  meConnectionsService,
  type ConnectionEnvironment,
  type LinkedAccount,
  type MeConnection,
} from "@/services/me-connections-service";

interface EditLinkedAccountDialogProps {
  conn: MeConnection;
  account: LinkedAccount;
  onClose: () => void;
  onSaved: () => void;
}

export function EditLinkedAccountDialog({
  conn,
  account,
  onClose,
  onSaved,
}: EditLinkedAccountDialogProps) {
  const [accountLabel, setAccountLabel] = useState(
    account.accountLabel || account.displayName || "",
  );
  const [description, setDescription] = useState(account.description ?? "");
  const [environment, setEnvironment] = useState<ConnectionEnvironment | "">(
    account.environment ?? "",
  );
  const [isDefault, setIsDefault] = useState(account.isDefault);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!accountLabel.trim()) {
      setError("Account name is required.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await meConnectionsService.updateAccountProfile(conn.provider, account.id, {
        accountLabel: accountLabel.trim(),
        description: description.trim(),
        environment,
        isDefault,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update account.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit {conn.displayName} account</DialogTitle>
          <DialogDescription>
            Add context so agents can choose the right account when more than one
            is connected.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="linked-account-label">Account name</Label>
            <Input
              id="linked-account-label"
              value={accountLabel}
              onChange={(event) => setAccountLabel(event.target.value)}
              placeholder="Work Gmail"
              disabled={busy}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="linked-account-description">Description for agents</Label>
            <Textarea
              id="linked-account-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Use this for customer and team communication."
              disabled={busy}
              rows={3}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Environment</Label>
            <Select
              value={environment || "none"}
              onValueChange={(value) =>
                setEnvironment(value === "none" ? "" : (value as ConnectionEnvironment))
              }
              disabled={busy}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not specified</SelectItem>
                <SelectItem value="production">Production</SelectItem>
                <SelectItem value="staging">Staging</SelectItem>
                <SelectItem value="development">Development</SelectItem>
                <SelectItem value="sandbox">Sandbox</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <Checkbox
              checked={isDefault}
              onCheckedChange={(checked) => setIsDefault(checked === true)}
              disabled={busy}
            />
            Use by default for {conn.displayName}
          </label>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy}>
            {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
